/* eslint-disable no-restricted-syntax */
import {
  default as React,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Entity, RELATION_PART_OF } from '@backstage/catalog-model';
import {
  EntityFilter,
  useEntityList,
  catalogApiRef,
} from '@backstage/plugin-catalog-react';
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import { Select } from '@backstage/core-components';
import Box from '@material-ui/core/Box';

/**
 * EntityFilter that keeps only components whose RELATION_PART_OF
 * points to a system that belongs to the selected domain (business unit).
 *
 * Resolution chain: Domain -> Systems (via RELATION_PART_OF domain:X) -> Components
 *
 * When `active` is true and `allowedSystemRefs` is empty, the domain has
 * no systems — filterEntity returns false (show nothing).
 * When `active` is false the filter is cleared — filterEntity returns true.
 */
export class BusinessUnitEntityFilter implements EntityFilter {
  constructor(
    readonly allowedSystemRefs: string[],
    readonly active: boolean = true,
  ) {}

  filterEntity(entity: Entity): boolean {
    if (!this.active) return true;
    if (this.allowedSystemRefs.length === 0) return false;

    const systemRefs =
      entity.relations
        ?.filter(
          r => r.type === RELATION_PART_OF && r.targetRef.startsWith('system:'),
        )
        .map(r => r.targetRef) ?? [];

    return systemRefs.some(ref => this.allowedSystemRefs.includes(ref));
  }
}

type DomainEntry = {
  label: string;
  value: string; // entity ref, e.g. "domain:default/corporate"
};

/**
 * Dropdown filter for the catalog page that lets users filter components
 * by Business Unit (Domain). When a domain is selected, the filter resolves
 * which systems belong to that domain, then shows only components that are
 * part of those systems.
 */
export function BusinessUnitPicker() {
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const { updateFilters } = useEntityList();
  const [domains, setDomains] = useState<DomainEntry[]>([]);
  const [systemsByDomain, setSystemsByDomain] = useState<
    Record<string, string[]>
  >({});
  const [selected, setSelected] = useState<string>('all');
  const latestSelection = useRef<string>('all');

  // Fetch all Domain and System entities once on mount
  useEffect(() => {
    Promise.all([
      catalogApi.getEntities({
        filter: { kind: 'Domain' },
        fields: ['metadata.name', 'metadata.namespace', 'metadata.title'],
      }),
      catalogApi.getEntities({
        filter: { kind: 'System' },
        fields: ['metadata.name', 'metadata.namespace', 'relations'],
      }),
    ])
      .then(([domainsResp, systemsResp]) => {
        const entries: DomainEntry[] = domainsResp.items
          .map(e => ({
            label: e.metadata.title || e.metadata.name,
            value: `domain:${e.metadata.namespace || 'default'}/${
              e.metadata.name
            }`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setDomains(entries);

        // Build domain -> system refs mapping
        const mapping: Record<string, string[]> = {};
        for (const system of systemsResp.items) {
          const sysRef = `system:${system.metadata.namespace || 'default'}/${
            system.metadata.name
          }`;
          const domainRels =
            system.relations?.filter(
              r =>
                r.type === RELATION_PART_OF &&
                r.targetRef.startsWith('domain:'),
            ) ?? [];
          for (const rel of domainRels) {
            if (!mapping[rel.targetRef]) mapping[rel.targetRef] = [];
            mapping[rel.targetRef].push(sysRef);
          }
        }
        setSystemsByDomain(mapping);
      })
      .catch(() => {
        alertApi.post({
          message: 'Failed to load business unit filter options.',
          severity: 'error',
        });
      });
  }, [catalogApi, alertApi]);

  const handleChange = useCallback(
    (value: string | string[] | number | number[]) => {
      const stringValue = String(value);
      setSelected(stringValue);
      latestSelection.current = stringValue;

      if (stringValue === 'all') {
        updateFilters({
          businessUnit: undefined,
        } as Record<string, EntityFilter | undefined>);
        return;
      }

      const systemRefs = systemsByDomain[stringValue] ?? [];

      updateFilters({
        businessUnit: new BusinessUnitEntityFilter(systemRefs, true),
      } as Record<string, EntityFilter | undefined>);
    },
    [systemsByDomain, updateFilters],
  );

  const items = [
    { value: 'all', label: 'All' },
    ...domains.map(d => ({ value: d.value, label: d.label })),
  ];

  return (
    <Box pb={1} pt={1}>
      <Select
        label="Business Unit"
        items={items}
        selected={selected}
        onChange={handleChange}
      />
    </Box>
  );
}
